import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { routes } from '../routes';
import { useBranding } from '../context/BrandingContext';

const decodeReturnData = (value) => {
  if (!value) return {};
  try {
    const json = atob(value);
    return JSON.parse(json);
  } catch {
    return {};
  }
};

const STATUS_ALIAS = {
  paid: 'success',
  success: 'success',
  authorized: 'authorized',
  pending: 'checking',
  checking: 'checking',
  failed: 'error',
  error: 'error',
};

const STATUS_CONFIG = {
  checking: {
    title: 'Confirmando tu pago…',
    subtitle: 'Estamos validando la respuesta con Tilopay.',
    classes: 'bg-blue-50 text-blue-800 border-blue-200',
    icon: '⏳',
  },
  success: {
    title: '¡Pago confirmado!',
    subtitle: 'Recibirás un correo con los detalles de tu pedido.',
    classes: 'bg-green-50 text-green-800 border-green-200',
    icon: '✅',
  },
  authorized: {
    title: 'Pago autorizado',
    subtitle: 'Tu pago quedó autorizado y se capturará en breve.',
    classes: 'bg-amber-50 text-amber-800 border-amber-200',
    icon: '🕒',
  },
  error: {
    title: 'Estamos revisando tu pago',
    subtitle: 'No pudimos confirmarlo automáticamente, pero lo revisaremos de inmediato.',
    classes: 'bg-rose-50 text-rose-800 border-rose-200',
    icon: '⚠️',
  },
};

const CUSTOMER_COPY = {
  checking: {
    title: 'Estamos validando tu pago',
    description: 'Este proceso tarda solo unos segundos. Te avisaremos en pantalla cuando esté listo.',
    statusLabel: 'En verificación',
  },
  success: {
    title: 'Tu pago fue confirmado',
    description: 'Ya registramos tu pedido y recibirás un correo con todos los detalles.',
    statusLabel: 'Confirmado',
  },
  authorized: {
    title: 'Pago autorizado',
    description: 'El banco autorizó el pago. Finalizaremos la captura en los próximos minutos.',
    statusLabel: 'Autorizado',
  },
  error: {
    title: 'Estamos revisando tu pago',
    description: 'No pudimos validarlo automáticamente, pero ya recibimos los datos y te contactaremos si hace falta algo adicional.',
    statusLabel: 'En revisión',
  },
};

const getStatusVariant = (status) => STATUS_ALIAS[status] || 'checking';
const getStatusInfo = (status) => STATUS_CONFIG[getStatusVariant(status)] || STATUS_CONFIG.checking;
const getCustomerCopy = (status) =>
  CUSTOMER_COPY[getStatusVariant(status)] || CUSTOMER_COPY.checking;

const TILOPAY_LOGO_URL = 'https://jszgeoosgpozjlvohrjy.supabase.co/storage/v1/object/public/assets/logos/tilopay.png';

export default function TilopayReturnPage() {
  const { branding } = useBranding();
  const brandLogoUrl = branding.logos.light;
  const brandRgbString = branding.colors.primaryRgb || '90,97,229';
  const [brandR = 90, brandG = 97, brandB = 229] = brandRgbString
    .split(',')
    .map((value) => Number(value.trim()) || 0);
  const location = useLocation();
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const [showTechnical, setShowTechnical] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [state, setState] = useState({
    status: 'checking',
    message: '',
    details: null,
    lastError: '',
  });

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const queryData = useMemo(() => {
    const getParam = (...keys) => {
      for (const key of keys) {
        const val = params.get(key);
        if (val !== null) return val;
      }
      return null;
    };
    const raw = Object.fromEntries(params.entries());
    const returnData = getParam('returnData');
    const decodedReturn = decodeReturnData(returnData);
    return {
      code: getParam('code'),
      description: getParam('description'),
      orderCode: getParam('order'),
      tilopayTransaction: getParam('tilopay-transaction', 'tpt'),
      auth: getParam('auth'),
      orderHash: getParam('OrderHash'),
      returnData,
      decodedReturn,
      raw,
    };
  }, [params]);

  const identifiers = useMemo(() => {
    const decodedId =
      queryData.decodedReturn?.orderId ||
      queryData.decodedReturn?.id ||
      null;
    return {
      orderId: decodedId,
      orderCode: queryData.orderCode || decodedId || null,
    };
  }, [queryData]);

  useEffect(() => {
    let cancelled = false;

    const verifyPayment = async () => {
      if (!identifiers.orderId && !identifiers.orderCode) {
        setState({
          status: 'error',
          message: 'No recibimos datos suficientes para identificar tu pedido.',
          details: null,
          lastError: '',
        });
        return;
      }

      setState((prev) => ({
        ...prev,
        status: 'checking',
        message: 'Consultando con Tilopay…',
        lastError: '',
      }));

      try {
        const res = await fetch('/.netlify/functions/tilopay-consult', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderCode: identifiers.orderCode,
            orderId: identifiers.orderId,
            returnData: queryData.returnData,
            tilopayTransaction: queryData.tilopayTransaction,
            auth: queryData.auth,
            description: queryData.description,
            orderHash: queryData.orderHash,
            code: queryData.code,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'No pudimos validar el pago.');
        }
        if (cancelled) return;
        setState({
          status: data.paymentStatus || 'success',
          message: data.description || '',
          details: data,
          lastError: '',
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: 'error',
          message: 'No pudimos confirmar el pago automáticamente.',
          details: null,
          lastError: err.message || '',
        });
      }
    };

    verifyPayment();
    return () => {
      cancelled = true;
    };
  }, [
    identifiers.orderCode,
    identifiers.orderId,
    queryData.auth,
    queryData.description,
    queryData.orderHash,
    queryData.returnData,
    queryData.tilopayTransaction,
    refreshKey,
  ]);

  const variant = getStatusVariant(state.status);
  const statusInfo = getStatusInfo(state.status);
  const customerCopy = getCustomerCopy(state.status);
  const rawList = Object.entries(queryData.raw || {});
  const paymentStatusLabel = customerCopy.statusLabel;
  const hasTechnicalData = rawList.length > 0 || state.lastError;
  const showVoucher = variant === 'success' || variant === 'authorized';

  const orderFull = state.details?.orderId || identifiers.orderId || '';
  const orderShort = orderFull && String(orderFull).includes('-')
    ? String(orderFull).split('-')[0]
    : orderFull;
  const transactionId =
    state.details?.tilopayTransactionId || queryData.tilopayTransaction || '—';
  const amountDisplay = state.details?.amount
    ? `${state.details.amount} ${state.details.currency || ''}`
    : '';
  const lastChecked = state.details?.consultedAt
    ? new Date(state.details.consultedAt).toLocaleString('es-CR')
    : new Date().toLocaleString('es-CR');
  const canShowTechnicalToggle =
    hasTechnicalData && (variant === 'error' || variant === 'checking');

  const handleDownloadVoucher = async () => {
    const jsPDF = window.jspdf?.jsPDF;
    if (!jsPDF) {
      alert('No pudimos generar el comprobante. Intenta nuevamente.');
      return;
    }
    try {
      setDownloading(true);

      const fetchAsDataUrl = async (url) => {
        if (!url) return null;
        try {
          const res = await fetch(url, { mode: 'cors' });
          const blob = await res.blob();
          return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch {
          return null;
        }
      };

      const [brandLogo, tilopayLogo] = await Promise.all([
        fetchAsDataUrl(brandLogoUrl),
        fetchAsDataUrl(TILOPAY_LOGO_URL),
      ]);

      const doc = new jsPDF({ unit: 'mm', format: [80, 150] });

      // Encabezado
      doc.setFillColor(brandR, brandG, brandB);
      doc.rect(0, 0, 80, 40, 'F');
      if (brandLogo) {
        const logoSize = 24;
        const logoX = (80 - logoSize) / 2;
        const logoY = (40 - logoSize) / 2;
        doc.addImage(brandLogo, 'PNG', logoX, logoY, logoSize, logoSize, undefined, 'FAST');
      }
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text('Comprobante de pago', 40, 34, { align: 'center' });

      // Cuerpo
      doc.setTextColor(26, 32, 44);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const startY = 50;
      const lineHeight = 8;
      const rows = [
        ['Pedido', orderShort || orderFull || '-'],
        ['Transacción', transactionId],
        ['Monto', amountDisplay || '—'],
        ['Estado', paymentStatusLabel],
        ['Fecha', lastChecked],
      ];
      rows.forEach(([label, value], idx) => {
        const y = startY + idx * lineHeight;
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}:`, 5, y);
        doc.setFont('helvetica', 'normal');
        doc.text(String(value || '—'), 28, y);
        doc.setDrawColor(235, 237, 240);
        doc.line(5, y + 2, 75, y + 2);
      });

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(brandR, brandG, brandB);
      doc.text('Gracias por comprar en El Ancla CR', 40, 100, { align: 'center' });
      doc.setTextColor(26, 32, 44);
      doc.text('Procesado con Tilopay', 40, 107, { align: 'center' });
      if (tilopayLogo) {
        doc.addImage(tilopayLogo, 'PNG', 20, 111, 40, 9, undefined, 'FAST');
      }
      doc.setFontSize(7);
      doc.setTextColor(120, 125, 133);
      doc.text('Guardá este comprobante como respaldo.', 40, 130, { align: 'center' });

      doc.save(`comprobante-${orderShort || 'pedido'}.pdf`);
    } catch (err) {
      console.error('Voucher pdf error:', err);
      alert('No pudimos generar el comprobante. Intenta nuevamente.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-gray-50 py-12 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto bg-white shadow-2xl rounded-[32px] p-6 sm:p-10 border border-gray-100">
        <div className="text-center mb-8 space-y-3">
          <p className="text-5xl" aria-hidden="true">
            {statusInfo.icon}
          </p>
          <h1 className="text-3xl font-bold text-gray-900">
            {customerCopy.title}
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">{customerCopy.description}</p>
        </div>

        {variant !== 'error' ? (
          <div className="rounded-[28px] border border-gray-100 bg-gradient-to-b from-white to-gray-50 p-6 sm:p-8 mb-8 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <SummaryItem label="Pedido" value={orderFull || 'En proceso'} />
              <SummaryItem
                label="Estado"
                value={paymentStatusLabel}
                highlight
                note={state.message || queryData.description || ''}
              />
              <SummaryItem
                label="Monto"
                value={amountDisplay || 'Pendiente'}
              />
              <SummaryItem
                label="Transacción Tilopay"
                value={transactionId || 'En espera'}
              />
            </div>
            <div className="text-sm text-gray-500">
              Última revisión: {lastChecked}
            </div>
            {showVoucher && (
              <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white/70 p-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Comprobante disponible</p>
                  <p className="text-xs text-gray-600">
                    Guardá este documento como respaldo del pago.
                  </p>
                </div>
                <button
                  onClick={handleDownloadVoucher}
                  disabled={downloading}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 text-white px-4 py-3 text-sm font-semibold shadow hover:bg-gray-800 disabled:opacity-50"
                >
                  {downloading ? 'Generando…' : 'Descargar comprobante'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-[28px] border border-rose-100 bg-rose-50 p-6 sm:p-8 mb-8 text-sm text-rose-800">
            <p className="font-semibold mb-2">Recibimos tus datos</p>
            <p>
              No pudimos confirmar el pago en línea, pero nuestro equipo ya recibió el reporte con todos los
              detalles. Te contactaremos en caso de necesitar información adicional o para confirmar manualmente.
            </p>
          </div>
        )}

        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate(routes.misPedidos)}
            className="px-6 py-3 rounded-xl bg-brand text-white font-semibold shadow hover:bg-brand/90"
          >
            Ver mis pedidos
          </button>
          <button
            onClick={() => navigate(routes.home)}
            className="px-6 py-3 rounded-xl border border-gray-300 text-gray-800 font-semibold hover:bg-gray-50"
          >
            Seguir comprando
          </button>
        </div>

        <div className="mt-8 flex flex-col items-center gap-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setRefreshKey((prev) => prev + 1)}
              className="px-5 py-3 rounded-xl border border-gray-200 text-gray-800 hover:bg-gray-50 text-sm font-semibold"
            >
              Reintentar verificación
            </button>
            {canShowTechnicalToggle && (
              <button
                onClick={() => setShowTechnical((prev) => !prev)}
                className="px-5 py-3 rounded-xl bg-gray-900 text-white text-sm font-semibold shadow hover:bg-gray-800"
              >
                {showTechnical ? 'Ocultar detalles técnicos' : 'Ver detalles técnicos'}
              </button>
            )}
          </div>

          {showTechnical && hasTechnicalData && (
            <div className="w-full rounded-2xl border border-gray-200 bg-gray-50 p-5 text-sm text-gray-700 space-y-4">
              <p className="font-semibold text-gray-900">Diagnóstico</p>
              {state.lastError && (
                <div className="rounded-xl bg-white border border-gray-200 p-3 font-mono text-xs text-gray-600 overflow-x-auto">
                  {state.lastError}
                </div>
              )}
              {rawList.length > 0 && (
                <details open className="rounded-xl border border-gray-200 bg-white p-3">
                  <summary className="cursor-pointer font-semibold text-gray-900">
                    Parámetros del redirect
                  </summary>
                  <dl className="mt-3 space-y-1 text-xs">
                    {rawList.map(([key, value]) => (
                      <div key={key} className="flex justify-between gap-3">
                        <dt className="text-gray-500">{key}</dt>
                        <dd className="font-mono break-all text-gray-800">{value || '—'}</dd>
                      </div>
                    ))}
                  </dl>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryItem({ label, value, note, highlight }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white px-4 py-5 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`text-lg font-semibold ${highlight ? 'text-gray-900' : 'text-gray-800'}`}>
        {value || '—'}
      </p>
      {note && <p className="text-xs text-gray-500 mt-1">{note}</p>}
    </div>
  );
}
